import {
  Calendar,
  Award,
  DollarSign,
  GraduationCap,
  Stethoscope,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { DoctorDto } from "@/src/web-api-client";
import { DoctorImage } from "../../doctor/DoctorImage";
import { useLanguage } from "../../../i18n/LanguageContext";

interface DoctorCardProps {
  doctor: DoctorDto;
  onBookAppointment: (doctor: DoctorDto) => void;
  onViewDetails: (doctor: DoctorDto) => void;
  hasAvatar?: string | null;
  avatarError?: string | undefined;
}

export const DoctorCard = ({
  doctor,
  onBookAppointment,
  onViewDetails,
  hasAvatar,
  avatarError,
}: DoctorCardProps) => {
  const processed = hasAvatar
    ? (doctor.processedUrl ?? null)
    : (doctor.originalUrl ?? null);

  const imgHeight = avatarError
    ? "h-auto"
    : processed && processed.trim() !== ""
      ? "h-full"
      : "";

  const { t } = useLanguage();
  const hasSchedule = !!doctor.daysThisWeek && doctor.daysThisWeek.length > 0;

  const doctorDisplayName = [
    doctor?.title && `${doctor.title.replace(/\.*$/, "")}.`,
    doctor?.name ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-[1px] hover:shadow-[var(--shadow-hover)]">
      <div className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
        <div
          className={`relative flex w-[100px] shrink-0 items-center justify-center sm:w-24 bg-[#f4f7fb] ${!processed && "p-1 rounded-lg min-h-[100px] "}`}
        >
          <DoctorImage
            doctor={doctor}
            hasAvatar={hasAvatar}
            avatarError={avatarError}
            className={`rounded-lg ${imgHeight} transition-colors duration-300 group-hover:border-primary/80`}
          />
        </div>

        <div className="min-w-0 flex-1">
          <span className="line-clamp-2 text-base font-semibold leading-snug text-primary sm:text-lg">
            {doctor?.name}
          </span>

          <div className="mt-2.5 space-y-2 text-sm sm:mt-3 sm:space-y-2.5">
            <div className="grid grid-cols-[80px_minmax(0,1fr)] md:grid-cols-[110px_minmax(0,1fr)] items-start gap-x-2 sm:grid-cols-[84px_minmax(0,1fr)]">
              <div className="flex items-center gap-1.5 text-foreground/80">
                <GraduationCap className="hidden h-5 w-5 text-primary sm:block" />
                <span className="truncate text-sm sm:text-sm">
                  {t("chucdanh")}:
                </span>
              </div>

              <div className="min-w-0">
                {doctor.title && doctor.title !== "" ? (
                  <span className="block line-clamp-1 text-sm text-foreground sm:text-sm">
                    {doctor.title}
                  </span>
                ) : (
                  <span className="block line-clamp-1 text-sm text-muted-foreground sm:text-sm">
                    Đang cập nhật
                  </span>
                )}
              </div>
            </div>

            {doctor.specialty && doctor.specialty.trim() !== "" && (
              <div className="grid grid-cols-[95px_minmax(0,1fr)] md:grid-cols-[120px_minmax(0,1fr)] items-start gap-x-2 sm:grid-cols-[84px_minmax(0,1fr)]">
                <div className="flex items-center gap-1.5 text-foreground/80">
                  <Stethoscope className="hidden h-5 w-5 text-primary sm:block" />
                  <span className="truncate text-sm sm:text-sm">
                    {t("chuyenkhoa")}:
                  </span>
                </div>

                <div className="min-w-0">
                  <span className="block line-clamp-2 text-sm text-foreground sm:text-sm">
                      {doctor.specialty}
                    </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-[80px_minmax(0,1fr)] md:grid-cols-[110px_minmax(0,1fr)] items-start gap-x-2 sm:grid-cols-[84px_minmax(0,1fr)]">
              <div className="flex items-center gap-1.5 text-foreground/80">
                <Calendar className="hidden h-5 w-5 text-primary sm:block" />
                <span className="truncate text-sm sm:text-sm">
                  {t("lichkham")}:
                </span>
              </div>

              <div className="min-w-0">
                {hasSchedule ? (
                  <div className="flex flex-wrap gap-1.5">
                    {doctor.daysThisWeek!.map((dayStr, idx) => (
                      <Badge
                        key={idx}
                        variant="secondary"
                        className="h-6 rounded-md border-0 bg-primary/10 px-2 text-sm font-medium text-primary sm:text-xs"
                      >
                        {dayStr}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <Badge
                    variant="secondary"
                    className="h-6 rounded-md border-0 bg-muted px-2 text-sm font-medium text-muted-foreground sm:text-xs"
                  >
                    Không có lịch
                  </Badge>
                )}
              </div>
            </div>

            <div className="grid grid-cols-[80px_minmax(0,1fr)] md:grid-cols-[110px_minmax(0,1fr)] items-center gap-x-2 sm:grid-cols-[84px_minmax(0,1fr)]">
              <div className="flex items-center gap-1.5 text-foreground/80">
                <DollarSign className="hidden h-5 w-5 text-primary sm:block" />
                <span className="truncate text-sm sm:text-sm">
                  {t("giakham")}
                </span>
              </div>

              <div className="text-sm font-bold text-green-700 sm:text-base">
                {(doctor.consultationFee ?? 1600000).toLocaleString("vi-VN")}đ
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 border-t border-border/80 p-3 sm:grid-cols-[120px_minmax(0,1fr)] sm:px-4 sm:py-3">
        <Button
          size="lg"
          variant="outline"
          className="h-9 rounded-md border-primary/20 px-3 text-sm font-medium text-primary hover:bg-primary/5 sm:h-10"
          onClick={() => onViewDetails(doctor)}
        >
          Thông tin
        </Button>

        <Button
          size="lg"
          className="h-9 rounded-md text-sm font-medium sm:h-10"
          onClick={() => onBookAppointment?.(doctor)}
          disabled={!hasSchedule}
        >
          <Calendar className="mr-1.5 h-4 w-4" />
          {hasSchedule ? "Đặt khám ngay" : "Hết lịch"}
        </Button>
      </div>
    </Card>
  );
};
